import React, { useState, useEffect, useRef } from 'react';
import Lottie from "lottie-react";

/**
 * LazyLottieLoader - Performance optimized Lottie animation loader
 * Only loads and plays animations when visible in viewport 
 * @param {object} animationData - Lottie animation JSON data
 * @param {string} placeholder - Optional static image to show before animation loads
 * @param {object} style - CSS styles
 * @param {string} className - CSS classes
 * @param {function} onComplete - Callback when animation completes
 * @param {boolean} loop - Whether animation should loop
 * @param {boolean} autoplay - Whether animation should autoplay
 * @param {number} speed - Animation playback speed
 */
const LazyLottieLoader = ({ 
  animationData, 
  placeholder = null, 
  style = {}, 
  className = "", 
  onComplete = () => {},
  loop = true,
  autoplay = true,
  speed = 1,
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  // Set up intersection observer to detect when animation enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Start loading when 10% of the container is visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.disconnect();
      }
    };
  }, []);
  
  // Preserve the original dimensions and layout from user-provided style
  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    ...style
  };

  const lottieStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  };

  return (
    <div 
      ref={containerRef}
      className={`lazy-lottie-container ${className}`}
      style={containerStyle}
    >
      {isVisible ? (
        <Lottie 
          animationData={animationData} 
          loop={loop}
          autoplay={autoplay}
          speed={speed}
          style={lottieStyle}
          onComplete={onComplete}
          {...props}
        />
      ) : placeholder ? (
        <div className="lazy-lottie-placeholder" style={lottieStyle}>
          {placeholder}
        </div>
      ) : (
        <div className="lazy-lottie-loading" style={{
          width: '100%', 
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div 
            style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              animation: 'lazyLottieSpin 1s linear infinite'
            }} 
          />
        </div>
      )}
    </div>
  );
};

// keyframes for the loading spinner if they don't exist
if (typeof window !== 'undefined' && !document.getElementById('lazy-lottie-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'lazy-lottie-styles';
  styleEl.innerHTML = `
    @keyframes lazyLottieSpin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleEl);
}

export default LazyLottieLoader;